import React, { useContext, useDebugValue, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";

import _ from "lodash";

import { DealRoomContext } from "../context";

import TinyMCE from 'react-tinymce/lib/components/TinyMCE';

import { animated, useTransition } from "@react-spring/web";
import { DEFAULT_FONTS } from "src/constants/fonts";
import { ReadMoreHtml } from "src/components/readMore";
import { MenuState } from "../../types";

import styles from './description.module.scss';
import contentStyle from './content.scss';
import { AssetCatergory, DealRoomEditState } from "src/types/dealRoom";
import DealRoomWidgetManager from "src/utils/dealRoom.widget.service";
import Button from "src/components/button";

const noAssets = "/images/dealRoom/no_assets.svg";
const noUsers = "/images/dealRoom/no_users.svg";

interface DealRoomDescriptionProps {
  dealRoom: DealRoomEditState,
  menuState: MenuState,
  disabled?: boolean,
  maxHeight?: string,
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void
}

const DealRoomDescription: React.FC<DealRoomDescriptionProps> = ({ dealRoom, menuState, disabled, maxHeight, onMenuChange }) => {
  const dealRoomContext = useContext(DealRoomContext);
  const editing = menuState.primaryAction !== 'view' && !disabled;

  const widgetManager = useRef(new DealRoomWidgetManager());

  const editorTransitions = useTransition(editing, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const viewerTransitions = useTransition(!editing, {
    initial: { opacity: 1 },
    from: { opacity: 1 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  const handleChange = (content: string) => {
    dealRoomContext.updateDealRoomProperty('description', content);
  }

  return (
    <div className={styles.container}>
      {editorTransitions((style, item) => item &&
        <animated.div style={style} className={styles.content}>
          <DescriptionEditor 
            dealRoom={dealRoom} 
            widgetManager={widgetManager.current} 
            maxHeight={maxHeight}
            menuState={menuState} 
            onChange={handleChange} 
            onMenuChange={onMenuChange} 
          />
        </animated.div>
      )}

      {viewerTransitions((style, item) => item &&
        <animated.div style={style} className={styles.content}>
          <DescriptionViewer dealRoom={dealRoom} widgetManager={widgetManager.current} maxHeight={maxHeight} />
        </animated.div>
      )}

    </div>
  );
}

interface DescriptionViewerProps {
  dealRoom: DealRoomEditState,
  widgetManager: DealRoomWidgetManager,
  maxHeight?: string,
}

const DescriptionViewer: React.FC<DescriptionViewerProps> = ({ dealRoom, widgetManager, maxHeight }) => {
  const account = useSelector((state: any) => _.get(state, 'accounts.item'));

  const totalAssetsCount = _.get(dealRoom, 'allAssetsCount', 0)

  const html = useMemo(() => {
    const description = dealRoom.description || "";

    const parameters = {
      contact: {
        firstname: '<First Name>',
        lastname: '<Last Name>',
      },
      collection: {
        name: dealRoom.name,
        assets: totalAssetsCount
      },
      company: {
        name: account.name
      }
    }

    return widgetManager.render(description, parameters, true);
  }, [dealRoom, account]);

  return (
    <ReadMoreHtml className={styles.viewer} style={{maxHeight: maxHeight}}>
      <div dangerouslySetInnerHTML={{ __html: html }} />
    </ReadMoreHtml>
  );
}

interface DescriptionEditorProps {
  dealRoom: DealRoomEditState,
  widgetManager: DealRoomWidgetManager,
  maxHeight?: string,
  menuState: MenuState,
  // eslint-disable-next-line no-unused-vars
  onChange: (content: string) => void
  // eslint-disable-next-line no-unused-vars
  onMenuChange: (value: MenuState) => void
}

export const DescriptionEditor: React.FC<DescriptionEditorProps> = ({ dealRoom, widgetManager, maxHeight, menuState, onMenuChange, onChange }) => {
  const editorRef = useRef<any>(null);
  const [initialized, setInitialized] = React.useState(false);

  const account = useSelector((state: any) => _.get(state, 'accounts.item'));

  const description = useMemo(() => {
    return dealRoom.description || "";
  }, []);

  const customfontStyle = _.get(account, 'theme.customStyleSheet', '');

  const validVariables = useMemo(() => {
    const excludedVariables = ["company.name", "signature.description"];
    return widgetManager.variables().filter(item => !excludedVariables.includes(item.id)).map((item) => {
      return item.id;
    });

  }, []);

  const bgColor = _.get(account, 'theme.primaryColor') || "#E51058";
  const buttonTextColor = _.get('account', 'theme.buttonTextColor') || "#FFFFFF";

  const fonts = useMemo(() => {
    let fonts = [...DEFAULT_FONTS];
    const customFonts = _.get(account, 'theme.fonts', []);

    if (customFonts && customFonts.length > 0) {
      fonts = [...customFonts, ...fonts];
    }

    return fonts.map(font => {
      return `${font.name}=${font.fontFamily}`
    }).join(';');
  }, [account]);

  useEffect(() => { 
    if(menuState.secondaryAction === 'rename'){
      onMenuChange({ 
        ...menuState, secondaryAction: null 
      });
    }
  }, [menuState.secondaryAction]);

  useEffect(() => { 
    if(editorRef.current && editorRef.current.getElement() !== null){
      editorRef.current.getElement().style.maxHeight = maxHeight;
    }
  }, [maxHeight, initialized]);

  const handleChange = (e) => {
    const template = widgetManager.getRawTemplate(e.target.getContent());
    onChange(template);
  }

  var toxTop = 300;
  useEffect(() => {
    const scrollContainer = document.getElementById("app-body");

    const handleScroll = () => {
      const editorContainer = document.getElementById("editorContainer");
      if (editorContainer) {
        toxTop = editorContainer.getBoundingClientRect().top;
        const tinymceInlineElements = document.querySelectorAll('.tox-tinymce-inline') as NodeListOf<HTMLElement>;
        tinymceInlineElements.forEach((element) => {
           element.style.setProperty("top", `${toxTop - 50}px`, "important");
        });
      }
    };

    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("scroll", handleScroll);
      }

      const tinymceInlineElements = document.querySelectorAll('.tox-tinymce-inline') as NodeListOf<HTMLElement>;
      tinymceInlineElements.forEach((element) => {
        element.style.setProperty("display", `none`, "important");
      });
    };
  }, []);
 
  return (
    <div className={styles.editor} id="editorContainer">

        <style>
          {`
            .tox-tinymce-inline {
              position: absolute !important;
              top: ${toxTop}px !important;
              z-index: 1000 !important;
            }
          `}
        </style>

      <TinyMCE
        className={styles.tinyEditor}
        content={description}
        config={{
          placeholder: "Type your description here...",
          inline: true,
          branding: false,
          statusbar: false,
          contextmenu: false,
          theme: 'silver',
          plugins: 'code, autoresize, noneditable, variable, paste, link, autolink, lists',
          menubar: false,
          toolbar: 'undo redo | bold italic | alignleft aligncenter alignright | fontselect | fontsizeselect | forecolor backcolor | code | link | cta | variables',
          fontsize_formats: '5px 5.5px 6.5px 7.5px 8px 9px 10px 10.5px 11px 12px 14px 16px 18px 20px 22px 24px 26px 28px 36px 48px 72px',
          autoresize_bottom_margin: 0,
          autoresize_min_height: 100,
          paste_retain_style_properties: "color",
          resize: false,
          min_height: 100,
          content_css: [
            'https://fonts.googleapis.com/css?family=Montserrat:100,100i,200,200i,300,300i,400,400i,500,500i,600,600i,700,700i,800,800i,900,900i',
            contentStyle, customfontStyle
          ],
          font_formats: fonts,
          default_link_target: "_blank",
          browser_spellcheck: true,
          convert_urls: false,
          relative_urls: false,
          remove_script_host: false,
          variable_mapper: (id) => {
            const variable = widgetManager.variables().find((item) => {
              return item.id == id;
            });

            if (variable) {
              return variable.name;
            }
          },
          variable_valid: validVariables,

          setup: (editor) => {
            editorRef.current = editor;
            
            editor.on('PostRender', () => {
              setTimeout(() => {
                if(editor?.getElement() !== null){
                  editor.getElement().style.maxHeight = maxHeight;
                }
                setInitialized(true);
              }, 1000);
            });

            editor.ui.registry.addButton("cta", {
              text: "CTA Button",
              onAction: () => {
                editor.insertContent(`<a href="#" class="collection_cta_button" style="outline: none; background: ${bgColor}; color: ${buttonTextColor}; padding: 10px 15px;">CLICK HERE</a>`)
              }
            });

            editor.ui.registry.addContextToolbar('ctaToolBar', {
              predicate: function (node) {
                return node.className === 'collection_cta_button'
              },
              items: 'forecolor backcolor link',
              position: 'line',
              scope: 'node'
            });

            editor.ui.registry.addButton('placeholder_label', {
              text: 'Placeholders: ',
              onAction: () => {

              },
            });

            editor.ui.registry.addMenuButton('variables', {
              text: 'Variables',
              fetch: (callback) => {
                const items: any[] = [];
                widgetManager.variables().forEach((variable) => {
                  if (_.includes(validVariables, variable.id)) {
                    items.push({
                      type: 'menuitem',
                      text: variable.name,
                      onAction: () => {
                        editor.plugins.variable.addVariable(variable.id);
                      }
                    });
                  }
                });

                callback(items);
              }
            });

            editor.on('click', (e) => {
              const node = e.target;
              if (node.className === "collection_cta_button") {
                editor.selection.select(node, true);
              }
            });
          }
        }}
        onChange={handleChange}
      />
    </div>
  );
}

export { DealRoomDescription }